import React, { memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { styled } from '@linaria/react';
import { MobileHeaderMenu } from 'components/MobileHeader/MobileHeaderMenu';
import { QuickFilters } from 'components/QuickFilters';
import { SearchBoxMobile } from 'components/SearchBox/Search';
import { SearchSummaryButtonWithStore } from 'components/SearchSummaryButtonWithStore';
import { useIsPageSearchBoxOpen } from 'hooks/searchBox';
import { getAnalyticsPageName } from 'modules/meta/selectors';
import { showPreferences } from 'modules/overlay/actions';
import { getIsSearchBoxOpen, getSearchBoxAncestor } from 'modules/searchBox/selectors';
import { closeSearchBox } from 'modules/searchBox/slice';
import { SearchBoxAncestor } from 'modules/searchBox/types';
import { toggle } from 'opticks';
import { getHomeUrlWithUTMParams } from 'utils/utmParams';
import { CompactHeader } from '@daedalus/atlas/CompactHeader';
import { useIsReferralProgramEnabled } from '@daedalus/atlas/hooks/useIsReferralProgramEnabled';
import { Icon } from '@daedalus/atlas/Icon';
import { useBrand } from '@daedalus/core/src/_web/brand/hooks/useBrand';
import { getUrlParams } from '@daedalus/core/src/_web/utils/url';
import { Page } from '@daedalus/core/src/analytics/types/Events';
import { getUser, selectAuthEnabled } from '@daedalus/core/src/auth/modules/selectors';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { isReactNativeWebView, postMessageToWebView, RNWebViewMessageTypes } from '@daedalus/core/src/native';
import { BrandLogo } from '@daedalus/shared/src/whiteLabel/BrandLogo/BrandLogo';
const BottomContentWrapper = /*#__PURE__*/styled('div')({
  name: "BottomContentWrapper",
  class: "bfpdczz",
  propsAsIs: false
});
export interface Props {
  className?: string;
  hasShadow?: boolean;
  hasDivider?: boolean;
  hasHeaderSearchSummary?: boolean;
  hasHeaderBottomContent?: boolean;
  hasHeaderMenu?: boolean;
  rightContent?: React.ReactElement;
  hasBackButton?: boolean;
  hasSearchProgressBar?: boolean;
  headerTitle?: React.ReactElement;
}
export const MobileHeaderContent = memo(({
  className,
  hasShadow = false,
  hasDivider = false,
  hasHeaderSearchSummary,
  hasHeaderBottomContent = false,
  hasHeaderMenu = true,
  rightContent,
  hasBackButton = false,
  headerTitle
}: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    brand,
    brandHasFilters,
    brandHasCollapsingHeader,
    brandIsVio
  } = useBrand();
  const user = useSelector(getUser);
  const authEnabled = useSelector(selectAuthEnabled);
  const isInviteFriendsTab = useIsReferralProgramEnabled(brand);
  const pageName = useSelector(getAnalyticsPageName);
  const activeAncestor = useSelector(getSearchBoxAncestor);
  const searchBoxIsOpen = useSelector(getIsSearchBoxOpen) && SearchBoxAncestor.Page === activeAncestor;
  const isSearchOnWebView = pageName === Page.Search && isReactNativeWebView();
  const isPageSearchBoxOpen = useIsPageSearchBoxOpen();
  const hasTopHeader = brandHasCollapsingHeader;
  const hasMainHeader = !brandHasCollapsingHeader || hasHeaderBottomContent;
  const homeURL = getHomeUrlWithUTMParams(false);
  const urlParams = getUrlParams();
  const onBackButtonClick = () => {
    if (isSearchOnWebView && searchBoxIsOpen) return dispatch(closeSearchBox());

    /**
     * Whenever a user navigates from Alert management to SRP WebView, the back button should
     * send users back to Alert Management
     */
    if (isSearchOnWebView && urlParams?.isAlertsManagementSearch === '1') {
      history.goBack();
      return;
    }

    /** Give control of the SRP back button to the mobile app, it will decide
     * whether to travel back to WebView homepage or the native home.
     *
     * It uses Opticks feature toggle to decide between the legacy history push
     * or posting a message to the mobile app.
     *
     * The toggle will decide the side based on the app version.
     *
     * TODO @FindHotel/mobile-squad [01-02-2024]: Remove this toggle by February
     */
    toggle('a5c18a26-feature-srp-back-button', () => {
      history.push(homeURL);
    }, () => {
      postMessageToWebView({
        type: RNWebViewMessageTypes.NAVIGATE,
        payload: {
          screen: 'Home'
        }
      });
    });
  };
  const handleShowPreferences = () => {
    dispatch(showPreferences({
      isInviteFriendsTab
    }));
  };
  const shouldShowUserAvatar = brandIsVio && authEnabled && !hasBackButton;
  const shouldShowEndContent = !isReactNativeWebView() && !brandHasCollapsingHeader && (rightContent || hasHeaderMenu);
  const hasNativeBack = isSearchOnWebView || hasBackButton;

  /**
   * When the SearchSummary is closed, it should expand from the center content towards the end content.
   * But when the search box is open, the center content will become "Edit your search" which should not expand.
   */
  const shouldExpandCenterContentInReactNative = isReactNativeWebView() && !searchBoxIsOpen;
  const nativeHeaderTitle = isSearchOnWebView && searchBoxIsOpen ? <FormattedMessageWrapper id="mapEditSearch" defaultMessage="Edit your search" /> : headerTitle;
  return <>
        {hasTopHeader && <CompactHeader className={className} brand={brand} hasDivider showStartLogo showTopContent endContent={<MobileHeaderMenu shouldShowUserAvatar={shouldShowUserAvatar} user={user} onShowPreferences={handleShowPreferences} />} logoUrl={homeURL} BrandLogoComponent={BrandLogo} hasBrandBackground />}
        {hasMainHeader && <CompactHeader className={className} hasExpandedCenterContent={brandHasCollapsingHeader || shouldExpandCenterContentInReactNative} brand={brand} hasShadow={hasShadow} hasDivider={hasDivider} onBackButtonClick={hasNativeBack && onBackButtonClick} showStartLogo={!brandHasCollapsingHeader} showTopContent={hasHeaderSearchSummary && !isPageSearchBoxOpen && !searchBoxIsOpen || !brandHasCollapsingHeader} backButtonIcon={isSearchOnWebView && searchBoxIsOpen ? <Icon name="Close" /> : <Icon name="ChevronLeft" />} centerContent={hasNativeBack && nativeHeaderTitle ? nativeHeaderTitle : hasHeaderSearchSummary && !isPageSearchBoxOpen && <SearchSummaryButtonWithStore ancestor={SearchBoxAncestor.Page} withSidePadding={!brandHasCollapsingHeader} />} endContent={shouldShowEndContent && <MobileHeaderMenu shouldShowUserAvatar={shouldShowUserAvatar} user={user} onShowPreferences={handleShowPreferences} />} bottomContent={hasHeaderBottomContent && <>
                  <BottomContentWrapper data-id="SearchBoxMobile" className={searchBoxIsOpen && '__padding'}>
                    <SearchBoxMobile openGuestPickerOnCheckOutChange={false} ancestor={SearchBoxAncestor.Page} />
                  </BottomContentWrapper>
                  {!searchBoxIsOpen && brandHasFilters && <QuickFilters />}
                </>} logoUrl={homeURL} BrandLogoComponent={BrandLogo} />}
      </>;
});
MobileHeaderContent.displayName = 'MobileHeaderContent';

require("./MobileHeaderContent.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./MobileHeaderContent.tsx");